import { useContext, useEffect, useRef, useState } from "react";

// importing components
import {
  Button,
  Col,
  Divider,
  Form,
  Input,
  message,
  Modal,
  Rate,
  Row,
  Select,
  Spin,
} from "antd";

// importing styles
import styles from "./ProductReviewInfo.module.scss";

// importing images
import dislikeIcon from "./images/dislike.svg";
import likeIcon from "./images/like.svg";
import uploadIcon from "./images/upload_icon.svg";
import star from "./images/star.svg";

// importing icons
import { FiEdit2 } from "react-icons/fi";
import { MdOutlineCancel } from "react-icons/md";
import axios from "../../config/axios";
import moment from "../../config/moment";
import { AuthContext } from "../../Contexts/AuthContext";

const ProductReviewInfo = ({ productId, selectedVariant }) => {
  const [activeWindow, setActiveWindow] = useState("reviewList");
  const uploadInput = useRef(null);
  const { isAuthenticated, user } = useContext(AuthContext);

  // rating state
  const [rating, setRating] = useState(5);
  const [texture, setTexture] = useState(5);
  const [quality, setQuality] = useState(5);
  const [flavour, setFlavour] = useState(5);
  const [headline, setHeadline] = useState("");
  const [body, setBody] = useState("");
  const [addRevLoading, setAddRevLoading] = useState(false);
  const [reviewFiles, setReviewFiles] = useState([]);
  const [imageUploadLoading, setImageUploadLoading] = useState(false);

  // review list states
  const [getRevLoading, setGetRevLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [page, setPage] = useState(1);
  const [moreLoading, setMoreLoading] = useState(false);

  // filter states
  const [reviewFilter, setReviewFilter] = useState({
    star: null,
  });

  // report abuse modal states
  const [reportAbuseModalVisible, setReportAbuseModalVisible] = useState(false);
  const [reportAbuseModalId, setReportAbuseModalId] = useState(null);

  // get review count per star
  const [starCount, setStarCount] = useState({
    five_star: 0,
    four_star: 0,
    one_star: 0,
    three_star: 0,
    total_reviews: 0,
    two_star: 0,
  });

  // edit review states
  const [editRevStates, setEditRevStates] = useState({
    product: productId,
    texture: 0,
    quality: 0,
    flavour: 0,
    rating: 0,
    headline: "",
    body: "",
    images: null,
    review_id: null,
  });
  const [editRevLoading, setEditRevLoading] = useState(false);

  const [refreshX, setRefreshX] = useState(null);

  useEffect(() => {
    (async () => {
      const res = await axios.get(`/public/reviews/ratings/${productId}`);
      setStarCount(res.data.review_count);
    })();
  }, [productId, refreshX]);

  //defining imageUploadHandler function
  const imageUploadHandler = async (e) => {
    if (!isAuthenticated) {
      message.error("You need to login first!");
      return;
    }
    // get files from event
    const files = e.target.files;

    // running loop over files
    for (const file of files) {
      let formData = new FormData();
      formData.append("file", file);

      // request to server here
      try {
        setImageUploadLoading(true);
        const response = await axios.post(
          `/admin/fundamentals/upload-file`,
          formData,
          { withCredentials: true },
        );

        setReviewFiles((images) => images.concat(response.data.secure_url));
      } catch (error) {
        message.error("A server side error occurred!");
      } finally {
        setImageUploadLoading(false);
      }
    }
  };

  //defining imageUploadHandler function for edit review
  const imageUploadHandlerForEdit = async (e) => {
    if (!isAuthenticated) {
      message.error("You need to login first!");
      return;
    }
    // get files from event
    const files = e.target.files;

    // running loop over files
    for (const file of files) {
      let formData = new FormData();
      formData.append("file", file);

      // request to server here
      try {
        setImageUploadLoading(true);
        const response = await axios.post(
          `/admin/fundamentals/upload-file`,
          formData,
          { withCredentials: true },
        );

        setEditRevStates((prev) => ({
          ...prev,
          images: prev.images.concat(response.data.secure_url),
        }));
      } catch (error) {
        console.log(error);
        message.error("A server side error occurred!");
      } finally {
        setImageUploadLoading(false);
      }
    }
  };

  useEffect(() => {
    (async () => {
      try {
        if (page !== 1) {
          setMoreLoading(true);
        }
        const res = await axios.get("/public/reviews", {
          params: {
            product: productId,
            limit: 10,
            page,
            ...reviewFilter,
          },
        });
        if (page === 1) {
          setReviews(res.data.results);
        } else {
          setReviews((prev) => prev.concat(res.data.results));
        }
      } catch (error) {
        if (page === 1) return;
        message.error("No More Reviews Found!");
      } finally {
        setGetRevLoading(false);
        setMoreLoading(false);
      }
    })();
  }, [productId, page, reviewFilter, refreshX]);

  const submitReview = async () => {
    if (!isAuthenticated) {
      message.error("You need to login first!");
      return;
    }
    try {
      if (headline === "") {
        message.error("Please add headline");
        return;
      }
      if (body === "") {
        message.error("Please add body");
        return;
      }
      const payload = {
        product: productId,
        texture: texture,
        quality: quality,
        flavour: flavour,
        rating: rating,
        headline: headline,
        body: body,
        images: reviewFiles ? JSON.stringify(reviewFiles) : null,
      };
      setAddRevLoading(true);

      await axios.post("/public/reviews", payload, { withCredentials: true });

      message.success("Your review has been added!");
      setPage(1);
      setReviewFiles([]);
      setActiveWindow("reviewList");
      setRefreshX(Math.random());
    } catch (error) {
      if (error.response.status === 406) {
        message.error("You have already reviewed this product");
        setActiveWindow("reviewList");
      } else {
        message.error("Can't submit review right now! Try Again!");
      }
    } finally {
      setAddRevLoading(false);
    }
  };

  // edit review
  const submitEditReview = async () => {
    if (!isAuthenticated) {
      message.error("You need to login first!");
      return;
    }
    try {
      if (editRevStates.headline === "") {
        message.error("Please add headline");
        return;
      }
      if (editRevStates.body === "") {
        message.error("Please add body");
        return;
      }
      const payload = {
        product: productId,
        texture: editRevStates.texture,
        quality: editRevStates.quality,
        flavour: editRevStates.flavour,
        rating: editRevStates.rating,
        headline: editRevStates.headline,
        body: editRevStates.body,
        images: editRevStates.images
          ? JSON.stringify(editRevStates.images)
          : null,
      };
      setEditRevLoading(true);

      await axios.patch(`/public/reviews/${editRevStates.review_id}`, payload, {
        withCredentials: true,
      });

      message.success("Your review has been edited!");
      setPage(1);
      setActiveWindow("reviewList");
      setRefreshX(Math.random());
    } catch (error) {
      if (error.response.status === 406) {
        message.error("You have already reviewed this product");
        setActiveWindow("reviewList");
      } else {
        message.error("Can't submit review right now! Try Again!");
      }
    } finally {
      setEditRevLoading(false);
    }
  };

  const reviewOptions = [
    { label: "Most Recent", value: "most_recent" },
    { label: "5 Stars Only", value: "5" },
    { label: "4 Stars Only", value: "4" },
    { label: "3 Stars Only", value: "3" },
    { label: "2 Stars Only", value: "2" },
    { label: "1 Star Only", value: "1" },
  ];

  const handleReviewFilter = (value) => {
    if (value === "most_recent") {
      setReviewFilter((prev) => ({ ...prev, star: null }));
    } else {
      setReviewFilter((prev) => ({ ...prev, star: +value }));
    }
  };

  // handle review like
  const handleReviewLike = async (review) => {
    if (!isAuthenticated) {
      message.error("You need to login first!");
      return;
    }

    // parse review likes from stringified json
    const reviewLikes = review.likes ? JSON.parse(review.likes) : [];

    console.log(reviewLikes);

    // check if user has already liked the review
    const isLiked = reviewLikes.includes(review.user.id);

    if (isLiked) {
      message.error("You have already liked this review");
      return;
    }

    // add user id to review likes
    reviewLikes.push(review.user.id);

    // remove user id from review dislikes
    const reviewDisLikes = review.dislikes ? JSON.parse(review.dislikes) : [];
    const filteredDisLikes = reviewDisLikes.filter(
      (id) => id !== review.user.id,
    );

    // stringify review dislikes
    const dislikes = JSON.stringify(filteredDisLikes);

    // stringify review likes
    const likes = JSON.stringify(reviewLikes);

    // send request to server
    try {
      message.loading("Liking review...");
      await axios.patch(
        `/public/reviews/${review.id}`,
        { likes, dislikes },
        { withCredentials: true },
      );
      message.destroy();
      message.success("Review liked successfully!");
      setRefreshX(Math.random());
    } catch (error) {
      message.error("Can't like review right now! Try Again!");
    }
  };

  // handle review dislike
  const handleReviewDislike = async (review) => {
    if (!isAuthenticated) {
      message.error("You need to login first!");
      return;
    }

    // parse review dislikes from stringified json
    const reviewDisLikes = review.dislikes ? JSON.parse(review.dislikes) : [];

    console.log(reviewDisLikes);

    // check if user has already liked the review
    const isDisLiked = reviewDisLikes.includes(review.user.id);

    if (isDisLiked) {
      message.error("You have already disliked this review");
      return;
    }

    // add user id to review dislikes
    reviewDisLikes.push(review.user.id);

    // remove user id from review likes
    const reviewLikes = review.likes ? JSON.parse(review.likes) : [];
    const filteredLikes = reviewLikes.filter((id) => id !== review.user.id);

    // stringify review likes
    const likes = JSON.stringify(filteredLikes);

    // stringify review likes
    const dislikes = JSON.stringify(reviewDisLikes);

    // send request to server
    try {
      message.loading("Disliking review...");
      await axios.patch(
        `/public/reviews/${review.id}`,
        { likes, dislikes },
        { withCredentials: true },
      );
      message.destroy();
      message.success("Review disliked!");
      setRefreshX(Math.random());
    } catch (error) {
      message.error("Can't dislike review right now! Try Again!");
    }
  };

  return (
    <div className={styles.productReviewInfo}>
      {activeWindow === "reviewList" && (
        <>
          <Row>
            {/* For mobile view */}
            <Col xs={24} lg={0}>
              <Row>
                <Col span={18}>
                  <div className={styles.title}>Customer reviews</div>
                  <div className={styles.rateContainer}>
                    <Rate disabled allowHalf defaultValue={4.7} />
                    <span>({starCount.total_reviews} Reviews)</span>
                  </div>
                </Col>
                <Col span={6}>
                  <div className={styles.mobile_rev_create}>
                    <img
                      src="https://desi-fresh.b-cdn.net/components_static/ProductDescription/edit.svg"
                      alt="create"
                      onClick={() => setActiveWindow("writeReview")}
                    />
                  </div>
                </Col>
              </Row>
              <Row>
                <Col span={22}>
                  <div className={styles.ratingPercentage_container}>
                    <div className={styles.percent_list_wrapper}>
                      <div className={styles.percent_list}>
                        <div className={styles.percent_list_title}>5 Star</div>
                        <div className={styles.percent_list_bar}>
                          <div className={styles.bar_exc}></div>
                          <div></div>
                        </div>
                        <div className={styles.percent_list_amount}>
                          ({starCount.five_star})
                        </div>
                      </div>

                      <div className={styles.percent_list}>
                        <div className={styles.percent_list_title}>4 Star</div>
                        <div className={styles.percent_list_bar}>
                          <div style={{ width: "22.5rem" }}></div>
                          <div></div>
                        </div>
                        <div className={styles.percent_list_amount}>
                          ({starCount.four_star})
                        </div>
                      </div>

                      <div className={styles.percent_list}>
                        <div className={styles.percent_list_title}>3 Star</div>
                        <div className={styles.percent_list_bar}>
                          <div style={{ width: "18rem" }}></div>
                          <div></div>
                        </div>
                        <div className={styles.percent_list_amount}>
                          ({starCount.three_star})
                        </div>
                      </div>

                      <div className={styles.percent_list}>
                        <div className={styles.percent_list_title}>2 Star</div>
                        <div className={styles.percent_list_bar}>
                          <div style={{ width: "6rem" }}></div>
                          <div></div>
                        </div>
                        <div className={styles.percent_list_amount}>
                          ({starCount.two_star})
                        </div>
                      </div>

                      <div className={styles.percent_list}>
                        <div className={styles.percent_list_title}>1 Star</div>
                        <div className={styles.percent_list_bar}>
                          <div style={{ width: "4.5rem" }}></div>
                          <div></div>
                        </div>
                        <div className={styles.percent_list_amount}>
                          ({starCount.one_star})
                        </div>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            </Col>

            {/* for desktop view */}
            <Col xs={0} lg={24}>
              {/* for desktop */}
              <Row gutter={[30, 30]}>
                <Col span={6}>
                  {/* title */}
                  <div className={styles.title}>Customer review</div>

                  <div className={styles.reviewTop}>
                    {/* left part */}
                    <div className={styles.reviewTop_left}>
                      <div className={styles.rateContainer}>
                        <Rate disabled allowHalf defaultValue={4.7} />
                        <span>({starCount.total_reviews} Reviews)</span>
                      </div>
                    </div>
                    {/* right part */}
                  </div>

                  <div className={styles.ratingPercentage_container}>
                    <div className={styles.percent_list_wrapper}>
                      <div className={styles.percent_list}>
                        <div className={styles.percent_list_title}>5 Star</div>
                        <div className={styles.percent_list_bar}>
                          <div className={styles.bar_exc}></div>
                          <div></div>
                        </div>
                        <div className={styles.percent_list_amount}>
                          ({starCount.five_star})
                        </div>
                      </div>

                      <div className={styles.percent_list}>
                        <div className={styles.percent_list_title}>4 Star</div>
                        <div className={styles.percent_list_bar}>
                          <div style={{ width: "18rem" }}></div>
                          <div></div>
                        </div>
                        <div className={styles.percent_list_amount}>
                          ({starCount.four_star})
                        </div>
                      </div>

                      <div className={styles.percent_list}>
                        <div className={styles.percent_list_title}>3 Star</div>
                        <div className={styles.percent_list_bar}>
                          <div style={{ width: "14rem" }}></div>
                          <div></div>
                        </div>
                        <div className={styles.percent_list_amount}>
                          ({starCount.three_star})
                        </div>
                      </div>

                      <div className={styles.percent_list}>
                        <div className={styles.percent_list_title}>2 Star</div>
                        <div className={styles.percent_list_bar}>
                          <div style={{ width: "10rem" }}></div>
                          <div></div>
                        </div>
                        <div className={styles.percent_list_amount}>
                          ({starCount.two_star})
                        </div>
                      </div>

                      <div className={styles.percent_list}>
                        <div className={styles.percent_list_title}>1 Star</div>
                        <div className={styles.percent_list_bar}>
                          <div style={{ width: "4.5rem" }}></div>
                          <div></div>
                        </div>
                        <div className={styles.percent_list_amount}>
                          ({starCount.one_star})
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={styles.write_review_dt}>
                    <h3>Review this product</h3>
                    <p>Share your thoughts with other customers</p>
                    <Button
                      className={styles.cts_btn}
                      size="large"
                      onClick={() => setActiveWindow("writeReview")}
                    >
                      <span>
                        <strong>Write a Review</strong>
                      </span>
                      <FiEdit2 size={20} />
                    </Button>
                  </div>
                </Col>
                <Col span={18}>
                  <div className={styles.review_dt_top}>
                    <div className={styles.title}>Customer’s top reviews</div>
                    <div>
                      <Select
                        defaultValue="most_recent"
                        style={{
                          width: 170,
                        }}
                        onChange={handleReviewFilter}
                        options={reviewOptions}
                      />
                    </div>
                  </div>

                  <Divider
                    style={{
                      margin: "1rem 0",
                    }}
                  />

                  {/* review list- desktop version */}
                  <div className={styles.reviewListWrapper}>
                    {getRevLoading ? (
                      <div className={styles.loading}>
                        <Spin />
                      </div>
                    ) : (
                      <>
                        {reviews.length > 0 ? (
                          <>
                            {reviews.map((review, index) => (
                              <div className={styles.reviewList} key={index}>
                                <div>
                                  {/* author */}
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: "1rem",
                                    }}
                                  >
                                    {review.user.dp ? (
                                      <img
                                        className={styles.reviewList_author_img}
                                        src={review.user.dp}
                                        alt="author"
                                      />
                                    ) : (
                                      <img
                                        className={styles.reviewList_author_img}
                                        src={
                                          "https://www.gravatar.com/avatar/?d=mp"
                                        }
                                        alt="author"
                                      />
                                    )}
                                    <div>
                                      <div
                                        className={
                                          styles.reviewList_author_name
                                        }
                                      >
                                        <span>
                                          {review.user.first_name}{" "}
                                          {review.user.last_name}
                                        </span>
                                      </div>
                                      <div
                                        style={{
                                          display: "flex",
                                          gap: "0.5rem",
                                          alignItems: "center",
                                        }}
                                      >
                                        <img
                                          src="https://desi-fresh.b-cdn.net/misc/german_flag.svg"
                                          alt="german_flag"
                                          height={15}
                                        />
                                        <div>
                                          <strong>German</strong>
                                        </div>
                                      </div>
                                    </div>
                                    <div
                                      className={styles.reviewList_number_rate}
                                    >
                                      <Rate
                                        disabled
                                        allowHalf
                                        defaultValue={review.rating}
                                        count={1}
                                      />
                                      <span>
                                        <b style={{ marginLeft: 5 }}>
                                          {review.rating}
                                        </b>
                                      </span>
                                    </div>
                                  </div>

                                  {/* edit review */}
                                  {isAuthenticated &&
                                    user.id === review.user.id && (
                                      <div
                                        className="mt-3 d-flex align-items-center gap-2"
                                        role="button"
                                        onClick={() => {
                                          setActiveWindow("editReview");
                                          setEditRevStates((prev) => ({
                                            ...prev,
                                            ...review,
                                            review_id: review.id,
                                            images: review.images
                                              ? JSON.parse(review.images)
                                              : null,
                                          }));
                                        }}
                                      >
                                        <FiEdit2
                                          className="text-primary"
                                          size={18}
                                        />
                                        <div className="text-primary fw-bold mt-2">
                                          Edit your review
                                        </div>
                                      </div>
                                    )}

                                  {/* given review number */}
                                  <div className={styles.reviewList_number}>
                                    <div className="mt-3">
                                      Review published on{" "}
                                      {moment(review.created_at).format(
                                        "DD MMMM YYYY",
                                      )}
                                    </div>
                                  </div>

                                  {/* review title */}
                                  <div className={styles.reviewList_title}>
                                    {" "}
                                    {review.headline}{" "}
                                  </div>

                                  {/* review body */}
                                  <div className={styles.reviewList_body}>
                                    {review.body}
                                  </div>

                                  {/* review feedback */}
                                  <div
                                    style={{
                                      marginTop: 10,
                                      marginBottom: 10,
                                    }}
                                  >
                                    <strong>This review is helpful?</strong>
                                  </div>

                                  <div className={styles.reviewList_feedback}>
                                    <div
                                      className={
                                        styles.reviewList_feedback_left
                                      }
                                    >
                                      <div className="d-flex align-items-center gap-2">
                                        <img
                                          src={likeIcon}
                                          alt="like"
                                          onClick={() => {
                                            handleReviewLike(review);
                                          }}
                                        />
                                        <span>
                                          (
                                          {
                                            JSON.parse(review.likes ?? "[]")
                                              .length
                                          }
                                          )
                                        </span>
                                      </div>

                                      <img
                                        src={dislikeIcon}
                                        alt="dislike"
                                        onClick={() => {
                                          handleReviewDislike(review);
                                        }}
                                      />
                                      <div
                                        className={
                                          styles.reviewList_feedback_right
                                        }
                                        onClick={() => {
                                          setReportAbuseModalVisible(true);
                                          setReportAbuseModalId(review.id);
                                        }}
                                      >
                                        Report Abuse
                                      </div>

                                      {reportAbuseModalVisible &&
                                        reportAbuseModalId === review.id && (
                                          <ReportAbuseModal
                                            visible={reportAbuseModalVisible}
                                            setVisible={
                                              setReportAbuseModalVisible
                                            }
                                            review={review}
                                            setRefreshX={setRefreshX}
                                          />
                                        )}
                                    </div>
                                  </div>
                                </div>
                                <div className={styles.rev_img_right}>
                                  <img
                                    src={
                                      review.images !== null &&
                                      review.images !== "[]"
                                        ? JSON.parse(review.images)[0]
                                        : selectedVariant.images[0]
                                    }
                                    alt="review_image"
                                  />
                                </div>
                              </div>
                            ))}
                          </>
                        ) : (
                          <div className="d-flex flex-column gap-3 align-items-center justify-content-center mt-5">
                            <div className="d-flex gap-2">
                              {Array.from({ length: 5 }).map((_, index) => (
                                <img
                                  src={star}
                                  alt="star"
                                  key={index}
                                  loading="lazy"
                                />
                              ))}
                            </div>
                            <p
                              className="text-center mb-0"
                              style={{
                                fontSize: 16,
                                fontWeight: 400,
                              }}
                            >
                              Would you like to share some feedback? <br />
                              Be the first to share product experience.
                            </p>

                            <Button
                              onClick={() => {
                                setActiveWindow("writeReview");
                              }}
                              type="primary"
                              size="large"
                              style={{
                                paddingInline: 50,
                              }}
                            >
                              Write a review
                            </Button>
                          </div>
                        )}
                      </>
                    )}

                    {reviews.length > 9 && (
                      <div className={styles.load_more_btn}>
                        <Button
                          onClick={() => {
                            setPage((prev) => prev + 1);
                          }}
                          loading={moreLoading}
                          disabled={moreLoading}
                        >
                          Load More
                        </Button>
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>

          {/* main review list section - mobile version */}
          <div
            className={`${styles.reviewListWrapper} ${styles.mobile_only}`}
            style={{ marginTop: 15 }}
          >
            <div className={styles.review_dt_top}>
              <div className={styles.title} style={{ marginTop: 0 }}>
                Customer’s top reviews
              </div>
              <div>
                <Select
                  defaultValue="most_recent"
                  style={{
                    width: 170,
                  }}
                  onChange={handleReviewFilter}
                  options={reviewOptions}
                />
              </div>
            </div>

            <Divider
              style={{
                margin: "1rem 0",
              }}
            />
            {getRevLoading ? (
              <div className={styles.loading}>
                <Spin />
              </div>
            ) : (
              <>
                {reviews.length > 0 ? (
                  <>
                    {reviews.map((review, index) => (
                      <div className={styles.reviewList} key={index}>
                        <div>
                          {/* author */}
                          <div
                            style={{
                              display: "flex",
                              gap: "1rem",
                            }}
                          >
                            {review.user.dp ? (
                              <img
                                className={styles.reviewList_author_img}
                                src={review.user.dp}
                                alt="author"
                              />
                            ) : (
                              <img
                                className={styles.reviewList_author_img}
                                src={"https://www.gravatar.com/avatar/?d=mp"}
                                alt="author"
                              />
                            )}

                            <div>
                              <div className={styles.reviewList_author_name}>
                                <span>
                                  {review.user.first_name}{" "}
                                  {review.user.last_name}
                                </span>
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  gap: "0.5rem",
                                  alignItems: "center",
                                }}
                              >
                                <img
                                  src="https://desi-fresh.b-cdn.net/misc/german_flag.svg"
                                  alt="german_flag"
                                  height={15}
                                />
                                <div>
                                  <strong>German</strong>
                                </div>
                              </div>
                            </div>
                            <div className={styles.reviewList_number_rate}>
                              <Rate
                                disabled
                                allowHalf
                                defaultValue={review.rating}
                                count={1}
                              />
                              <span>
                                <b style={{ marginLeft: 5 }}>{review.rating}</b>
                              </span>
                            </div>
                          </div>
                          {/* edit review */}
                          {isAuthenticated && user.id === review.user.id && (
                            <div
                              className="mt-3 d-flex align-items-center gap-2"
                              role="button"
                              onClick={() => {
                                setActiveWindow("editReview");
                                setEditRevStates((prev) => ({
                                  ...prev,
                                  ...review,
                                  review_id: review.id,
                                  images: review.images
                                    ? JSON.parse(review.images)
                                    : null,
                                }));
                              }}
                            >
                              <FiEdit2 className="text-primary" size={18} />
                              <div className="text-primary fw-bold mt-2">
                                Edit your review
                              </div>
                            </div>
                          )}
                          {/* given review number */}
                          <div className={styles.reviewList_number}>
                            <div
                              style={{
                                marginTop: 10,
                              }}
                            >
                              Review published on{" "}
                              {moment(review.created_at).format("DD MMMM YYYY")}
                            </div>
                          </div>

                          {/* review title */}
                          <div className={styles.reviewList_title}>
                            {" "}
                            {review.headline}{" "}
                          </div>

                          {/* review body */}
                          <div className={styles.reviewList_body}>
                            {review.body}
                          </div>

                          {/* review feedback */}
                          <div
                            style={{
                              marginTop: 10,
                              marginBottom: 10,
                            }}
                          >
                            <strong>This review is helpful?</strong>
                          </div>

                          <div className={styles.reviewList_feedback}>
                            <div className={styles.reviewList_feedback_left}>
                              <div className="d-flex align-items-center gap-2">
                                <img
                                  src={likeIcon}
                                  alt="like"
                                  onClick={() => {
                                    handleReviewLike(review);
                                  }}
                                />
                                <span>
                                  ({JSON.parse(review.likes ?? "[]").length})
                                </span>
                              </div>

                              <img
                                src={dislikeIcon}
                                alt="dislike"
                                onClick={() => {
                                  handleReviewDislike(review);
                                }}
                              />
                              <div
                                className={styles.reviewList_feedback_right}
                                onClick={() => {
                                  setReportAbuseModalVisible(true);
                                  setReportAbuseModalId(review.id);
                                }}
                              >
                                Report Abuse
                              </div>

                              {reportAbuseModalVisible &&
                                reportAbuseModalId === review.id && (
                                  <ReportAbuseModal
                                    visible={reportAbuseModalVisible}
                                    setVisible={setReportAbuseModalVisible}
                                    review={review}
                                    setRefreshX={setRefreshX}
                                  />
                                )}
                            </div>
                          </div>
                        </div>
                        <div className={styles.rev_img_right}>
                          <img
                            src={
                              review.images !== null && review.images !== "[]"
                                ? JSON.parse(review.images)[0]
                                : selectedVariant.images[0]
                            }
                            alt="review_image"
                          />
                        </div>
                      </div>
                    ))}
                  </>
                ) : (
                  <div className="d-flex flex-column gap-3 align-items-center justify-content-center mt-5">
                    <div className="d-flex gap-2">
                      {Array.from({ length: 5 }).map((_, index) => (
                        <img src={star} alt="star" key={index} loading="lazy" />
                      ))}
                    </div>
                    <p
                      className="text-center mb-0"
                      style={{
                        fontSize: 16,
                        fontWeight: 400,
                      }}
                    >
                      There are no customer reviews yet. <br />
                      Share your thoughts with our customers.
                    </p>

                    <Button
                      onClick={() => {
                        setActiveWindow("writeReview");
                      }}
                      type="primary"
                      style={{
                        paddingInline: 50,
                      }}
                    >
                      Write a review
                    </Button>
                  </div>
                )}
              </>
            )}

            {reviews.length > 9 && (
              <div className={styles.load_more_btn}>
                <Button
                  onClick={() => {
                    setPage((prev) => prev + 1);
                  }}
                  loading={moreLoading}
                  disabled={moreLoading}
                >
                  Load More
                </Button>
              </div>
            )}
          </div>
        </>
      )}

      {activeWindow === "writeReview" && (
        <div className={styles.writeReview_container}>
          <div
            className={styles.writeReview_title}
            style={{
              marginTop: 10,
            }}
          >
            <span
              style={{
                fontWeight: "bold",
                fontSize: 16,
                color: "var(--color-primary)",
              }}
            >
              Write a review
            </span>{" "}
            I <span>Completed Reviews/Feedback</span>
          </div>
          <Divider style={{ marginTop: 5 }} className="hide_mobile" />

          {/* user rating section where user will submit their rating */}
          <div className={styles.userRating_section}>
            {/* left */}
            <div className={styles.userRating_section_left}>
              <div style={{ fontWeight: "700" }}>Overall rating</div>
              <div>
                <Rate
                  style={{ fontSize: 30 }}
                  defaultValue={5}
                  onChange={(value) => {
                    setRating(value);
                  }}
                />
                <span style={{ fontWeight: "500", marginLeft: "1rem" }}>
                  {" "}
                  ( My rating ){" "}
                </span>
              </div>
              <div style={{ fontWeight: "500", marginTop: 10 }}>
                Product Features
              </div>
              <div className={styles.feature_star}>
                <div className={styles.feature_star_text}>Texture</div>
                <Rate
                  defaultValue={texture}
                  onChange={(value) => {
                    setTexture(value);
                  }}
                />
                <MdOutlineCancel
                  style={{ cursor: "pointer" }}
                  size={15}
                  onClick={() => {
                    console.log("clicked cross");
                  }}
                />
              </div>
              <div className={styles.feature_star}>
                <div className={styles.feature_star_text}>Quality</div>
                <Rate
                  defaultValue={quality}
                  onChange={(value) => {
                    setQuality(value);
                  }}
                />
                <MdOutlineCancel
                  style={{ cursor: "pointer" }}
                  size={15}
                  onClick={() => {
                    console.log("clicked cross");
                  }}
                />
              </div>
              <div className={styles.feature_star}>
                <div className={styles.feature_star_text}>Flavour</div>
                <Rate
                  defaultValue={flavour}
                  onChange={(value) => {
                    setFlavour(value);
                  }}
                />
                <MdOutlineCancel
                  style={{ cursor: "pointer" }}
                  size={15}
                  onClick={() => {
                    console.log("clicked cross");
                  }}
                />
              </div>
            </div>

            {/* right */}
            <div className={styles.userRating_section_right}>
              {/* scorecard rate table */}
              <div className={styles.scorecard_title}>Score Card</div>
              <div className={styles.scorecard_table}>
                <div className={styles.table_text}>Excellent</div>
                <Rate disabled defaultValue={5} />
              </div>
              <div className={styles.scorecard_table}>
                <div className={styles.table_text}>Good</div>
                <Rate disabled defaultValue={4} />
              </div>
              <div className={styles.scorecard_table}>
                <div className={styles.table_text}>Standard</div>
                <Rate disabled defaultValue={3} />
              </div>
              <div className={styles.scorecard_table}>
                <div className={styles.table_text}>Below Average</div>
                <Rate disabled defaultValue={2} />
              </div>
              <div className={styles.scorecard_table}>
                <div className={styles.table_text}>Poor</div>
                <Rate disabled defaultValue={1} />
              </div>
            </div>
          </div>

          {/* input form section */}
          <div className={styles.inputForm_section}>
            <div className={styles.input_headline}>
              <label htmlFor="headline">Add a headline</label>
              <Input
                value={headline}
                onChange={(e) => setHeadline(e.target.value)}
                showCount
                maxLength={30}
                placeholder="What is most important to know?"
              />
            </div>
            <div className={styles.input_review}>
              <label htmlFor="headline">My Review</label>
              <Input.TextArea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                showCount
                maxLength={500}
                rows={8}
                placeholder="Give your experience with the product use. What do you like or dislike and what was the use or purpose of this product."
              />
            </div>
            <div className={styles.input_file_wrapper}>
              <label htmlFor="files">Add a photo or video</label>
              <div className={styles.input_file_and_files}>
                {imageUploadLoading ? (
                  <div
                    style={{
                      height: 100,
                      width: 100,
                    }}
                    className="d-flex align-items-center justify-content-center"
                  >
                    <Spin size="large" />
                  </div>
                ) : (
                  <div className={styles.input_file}>
                    <input
                      ref={uploadInput}
                      type="file"
                      name="file"
                      hidden="hidden"
                      multiple={true}
                      onChange={imageUploadHandler}
                    />
                    <div
                      className={styles.upload_img}
                      onClick={() => {
                        if (!isAuthenticated) {
                          message.error("You need to login first!");
                          return;
                        }
                        uploadInput.current.click();
                      }}
                    >
                      <img src={uploadIcon} alt="upload" />
                    </div>
                  </div>
                )}

                <div className={styles.input_files}>
                  {reviewFiles?.map((file, index) => (
                    <img
                      src={file}
                      alt="file"
                      loading="lazy"
                      height={150}
                      width={150}
                      key={index}
                    />
                  ))}
                </div>
              </div>
            </div>

            <Button
              htmlType="submit"
              type="primary"
              size="large"
              danger
              onClick={submitReview}
              disabled={addRevLoading}
              loading={addRevLoading}
              className="mt-4 rounded-5 px-5"
            >
              Submit Now
            </Button>
          </div>
        </div>
      )}

      {/* edit review section */}
      {activeWindow === "editReview" && (
        <div className={styles.writeReview_container}>
          <div
            className={styles.writeReview_title}
            style={{
              marginTop: 10,
            }}
          >
            <span
              style={{
                fontWeight: "bold",
                fontSize: 16,
                color: "var(--color-primary)",
              }}
            >
              Write a review
            </span>{" "}
            I <span>Completed Reviews/Feedback</span>
          </div>
          <Divider style={{ marginTop: 5 }} className="hide_mobile" />

          {/* user rating section where user will submit their rating */}
          <div className={styles.userRating_section}>
            {/* left */}
            <div className={styles.userRating_section_left}>
              <div style={{ fontWeight: "700" }}>Overall rating</div>
              <div>
                <Rate
                  style={{ fontSize: 30 }}
                  defaultValue={editRevStates.rating}
                  onChange={(value) => {
                    setEditRevStates((prev) => ({
                      ...prev,
                      rating: value,
                    }));
                  }}
                />
                <span style={{ fontWeight: "500", marginLeft: "1rem" }}>
                  {" "}
                  ( My rating ){" "}
                </span>
              </div>
              <div style={{ fontWeight: "500", marginTop: 10 }}>
                Product Features
              </div>
              <div className={styles.feature_star}>
                <div className={styles.feature_star_text}>Texture</div>
                <Rate
                  defaultValue={editRevStates.texture}
                  onChange={(value) => {
                    setEditRevStates((prev) => ({
                      ...prev,
                      texture: value,
                    }));
                  }}
                />
                <MdOutlineCancel
                  style={{ cursor: "pointer" }}
                  size={15}
                  onClick={() => {
                    console.log("clicked cross");
                  }}
                />
              </div>
              <div className={styles.feature_star}>
                <div className={styles.feature_star_text}>Quality</div>
                <Rate
                  defaultValue={editRevStates.quality}
                  onChange={(value) => {
                    setEditRevStates((prev) => ({
                      ...prev,
                      quality: value,
                    }));
                  }}
                />
                <MdOutlineCancel
                  style={{ cursor: "pointer" }}
                  size={15}
                  onClick={() => {
                    console.log("clicked cross");
                  }}
                />
              </div>
              <div className={styles.feature_star}>
                <div className={styles.feature_star_text}>Flavour</div>
                <Rate
                  defaultValue={editRevStates.flavour}
                  onChange={(value) => {
                    setEditRevStates((prev) => ({
                      ...prev,
                      flavour: value,
                    }));
                  }}
                />
                <MdOutlineCancel
                  style={{ cursor: "pointer" }}
                  size={15}
                  onClick={() => {
                    console.log("clicked cross");
                  }}
                />
              </div>
            </div>

            {/* right */}
            <div className={styles.userRating_section_right}>
              {/* scorecard rate table */}
              <div className={styles.scorecard_title}>Score Card</div>
              <div className={styles.scorecard_table}>
                <div className={styles.table_text}>Excellent</div>
                <Rate disabled defaultValue={5} />
              </div>
              <div className={styles.scorecard_table}>
                <div className={styles.table_text}>Good</div>
                <Rate disabled defaultValue={4} />
              </div>
              <div className={styles.scorecard_table}>
                <div className={styles.table_text}>Standard</div>
                <Rate disabled defaultValue={3} />
              </div>
              <div className={styles.scorecard_table}>
                <div className={styles.table_text}>Below Average</div>
                <Rate disabled defaultValue={2} />
              </div>
              <div className={styles.scorecard_table}>
                <div className={styles.table_text}>Poor</div>
                <Rate disabled defaultValue={1} />
              </div>
            </div>
          </div>

          {/* input form section */}
          <div className={styles.inputForm_section}>
            <div className={styles.input_headline}>
              <label htmlFor="headline">Add a headline</label>
              <Input
                value={editRevStates.headline}
                onChange={(e) => {
                  setEditRevStates((prev) => ({
                    ...prev,
                    headline: e.target.value,
                  }));
                }}
                showCount
                maxLength={30}
                placeholder="What is most important to know?"
              />
            </div>
            <div className={styles.input_review}>
              <label htmlFor="headline">My Review</label>
              <Input.TextArea
                value={editRevStates.body}
                onChange={(e) => {
                  setEditRevStates((prev) => ({
                    ...prev,
                    body: e.target.value,
                  }));
                }}
                showCount
                maxLength={500}
                rows={8}
                placeholder="Give your experience with the product use. What do you like or dislike and what was the use or purpose of this product."
              />
            </div>
            <div className={styles.input_file_wrapper}>
              <label htmlFor="files">Add a photo or video</label>
              <div className={styles.input_file_and_files}>
                {imageUploadLoading ? (
                  <div
                    style={{
                      height: 100,
                      width: 100,
                    }}
                    className="d-flex align-items-center justify-content-center"
                  >
                    <Spin size="large" />
                  </div>
                ) : (
                  <div className={styles.input_file}>
                    <input
                      ref={uploadInput}
                      type="file"
                      name="file"
                      hidden="hidden"
                      multiple={true}
                      onChange={imageUploadHandlerForEdit}
                    />
                    <div
                      className={styles.upload_img}
                      onClick={() => {
                        if (!isAuthenticated) {
                          message.error("You need to login first!");
                          return;
                        }
                        uploadInput.current.click();
                      }}
                    >
                      <img src={uploadIcon} alt="upload" />
                    </div>
                  </div>
                )}

                <div className={styles.input_files}>
                  {editRevStates.images?.map((file, index) => (
                    <img
                      src={file}
                      alt="file"
                      loading="lazy"
                      height={150}
                      width={150}
                      key={index}
                    />
                  ))}
                </div>
              </div>
            </div>

            <Button
              htmlType="submit"
              type="primary"
              size="large"
              danger
              onClick={submitEditReview}
              disabled={editRevLoading}
              loading={editRevLoading}
              className="mt-4 rounded-5 px-5"
            >
              Edit Review
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductReviewInfo;

const ReportAbuseModal = ({ visible, setVisible, review, setRefreshX }) => {
  const [loading, setLoading] = useState(false);

  // handle review report abuse
  const handleReviewReportAbuse = async (values) => {
    // parse review reports from stringified json
    const reviewReports = review.reports ? JSON.parse(review.reports) : [];

    // check if user already reported this review
    const alreadyReported = reviewReports.find(
      (report) => report.user === review.user.id,
    );

    // if user already reported this review
    if (alreadyReported) {
      message.error("You already reported this review!");
      return;
    }

    // if user didn't report this review
    // add user to review reports
    reviewReports.push({
      user: review.user.id,
      message: values.message,
    });

    // stringify review reports
    const reports = JSON.stringify(reviewReports);

    // update review reports
    try {
      setLoading(true);
      await axios.patch(
        `/public/reviews/${review.id}`,
        { reports },
        { withCredentials: true },
      );
      message.success("Your report is submitted!");
      setRefreshX(Math.random());
    } catch (error) {
      message.error("Can't report abuse right now! Try again later!");
    } finally {
      setLoading(false);
      setVisible(false);
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={() => {
        setVisible(false);
      }}
      centered
      footer={null}
      title="Report Abuse"
    >
      <Form
        onFinish={handleReviewReportAbuse}
        layout="vertical"
        scrollToFirstError
        requiredMark={false}
      >
        <Form.Item
          name="message"
          label="Describe Your Message"
          rules={[
            {
              required: true,
              message: "Please describe your issue!",
            },
          ]}
        >
          <Input.TextArea />
        </Form.Item>

        <Form.Item className="mb-0">
          <Button
            type="primary"
            htmlType="submit"
            block
            loading={loading}
            disabled={loading}
          >
            Send
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};
